// Complete fixed BikeSelector component:

import React, { useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import type { BikeWithLocation } from "../../types/bike";
import { cn } from "../../utils/helpers";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";

interface BikeSelectorProps {
  bikes: BikeWithLocation[];
  selectedBikes: string[];
  onChange: (selectedBikes: string[]) => void;
}

export const BikeSelector: React.FC<BikeSelectorProps> = ({
  bikes,
  selectedBikes,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { getCategoryById } = useVehicleCategoryStore();

  const filteredBikes = bikes.filter((bike) =>
    bike.bike_id.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleBikeToggle = (bikeId: string) => {
    if (selectedBikes.includes(bikeId)) {
      onChange(selectedBikes.filter((id) => id !== bikeId));
    } else {
      onChange([...selectedBikes, bikeId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedBikes.length === filteredBikes.length) {
      onChange([]);
    } else {
      onChange(filteredBikes.map((bike) => bike.bike_id));
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors min-w-[200px]"
      >
        <span className="text-sm">
          {selectedBikes.length === 0
            ? "Select Vehicles"
            : `${selectedBikes.length} vehicle${
                selectedBikes.length > 1 ? "s" : ""
              } selected`}
        </span>
        <ChevronDown
          className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-[9999]">
            {/* Search */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Select All */}
            <div className="p-3 border-b border-gray-200">
              <button
                onClick={handleSelectAll}
                className="flex items-center space-x-3 w-full p-2 hover:bg-gray-100 rounded text-sm transition-colors"
              >
                <div
                  className={cn(
                    "w-4 h-4 border-2 rounded flex items-center justify-center transition-all",
                    selectedBikes.length === filteredBikes.length &&
                      filteredBikes.length > 0
                      ? "bg-blue-600 border-blue-600"
                      : "border-gray-300 bg-white",
                  )}
                >
                  {selectedBikes.length === filteredBikes.length &&
                    filteredBikes.length > 0 && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                </div>
                <span className="font-medium">
                  Select All ({filteredBikes.length})
                </span>
              </button>
            </div>

            {/* Bike List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredBikes.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {searchTerm ? "No bikes found" : "No bikes available"}
                </div>
              ) : (
                filteredBikes.map((bike) => {
                  const isSelected = selectedBikes.includes(bike.bike_id);

                  return (
                    <button
                      key={bike.bike_id}
                      onClick={() => handleBikeToggle(bike.bike_id)}
                      className="flex items-center space-x-3 w-full p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      {/* Fixed Checkbox */}
                      <div
                        className={cn(
                          "w-4 h-4 border-2 rounded flex items-center justify-center transition-all flex-shrink-0",
                          isSelected
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300 bg-white hover:border-blue-400",
                        )}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>

                      {/* Bike Info */}
                      <div className="flex-1 text-left min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {bike.bike_id}
                          <span className="text-xs text-gray-500 truncate ml-2">
                            {getCategoryById(bike.category_id)}
                          </span>
                        </div>
                      </div>

                      {/* Status Indicator */}
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full flex-shrink-0",
                          bike.status === "active"
                            ? "bg-green-500"
                            : bike.status === "maintenance"
                              ? "bg-red-500"
                              : "bg-gray-400",
                        )}
                      />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
